'use client'

// Icons
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'

// Utils
import { Join } from '../../lib/utils'

/**
 * Unified media taxonomy shared with `design-studio/data/imandra-media`.
 * `research` / `media` are kept as aliases for backward compatibility with
 * existing docs MDX.
 */
export type MediaDocCardType =
  | 'news'
  | 'article'
  | 'blog'
  | 'video'
  | 'paper'
  | 'white-paper'
  | 'research'
  | 'media'

const TYPE_CONFIG: Record<
  MediaDocCardType,
  { label: string; tagClass: string }
> = {
  video: {
    label: 'Video',
    tagClass:
      'text-red-600 dark:text-red-400 border-red-300 dark:border-red-400/30 bg-red-50 dark:bg-red-400/10',
  },
  paper: {
    label: 'Paper',
    tagClass:
      'text-violet-600 dark:text-violet-400 border-violet-300 dark:border-violet-400/30 bg-violet-50 dark:bg-violet-400/10',
  },
  research: {
    label: 'Research',
    tagClass:
      'text-violet-600 dark:text-violet-400 border-violet-300 dark:border-violet-400/30 bg-violet-50 dark:bg-violet-400/10',
  },
  'white-paper': {
    label: 'White paper',
    tagClass:
      'text-violet-600 dark:text-violet-400 border-violet-300 dark:border-violet-400/30 bg-violet-50 dark:bg-violet-400/10',
  },
  article: {
    label: 'Article',
    tagClass:
      'text-blue-600 dark:text-blue-400 border-blue-300 dark:border-blue-400/30 bg-blue-50 dark:bg-blue-400/10',
  },
  blog: {
    label: 'Blog',
    tagClass:
      'text-teal-600 dark:text-teal-400 border-teal-300 dark:border-teal-400/30 bg-teal-50 dark:bg-teal-400/10',
  },
  news: {
    label: 'News',
    tagClass:
      'text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-400/30 bg-amber-50 dark:bg-amber-400/10',
  },
  media: {
    label: 'Media',
    tagClass:
      'text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-400/30 bg-amber-50 dark:bg-amber-400/10',
  },
}

export interface MediaDocCardProps {
  type: MediaDocCardType
  title: string
  href: string
  date: string
  description?: string
  source?: string
  highlight?: string
}

export function MediaDocCard({
  type,
  title,
  description,
  href,
  source,
  date,
  highlight,
}: MediaDocCardProps) {
  const config = TYPE_CONFIG[type] ?? TYPE_CONFIG.article
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={Join(
        'group flex flex-col rounded-xl border p-5 no-underline transition-all duration-200',
        'border-slate-200 bg-slate-50/80 dark:border-slate-700/40 dark:bg-slate-800/20 dark:backdrop-blur-sm',
        'hover:border-teal-400/70 hover:bg-slate-100 dark:hover:bg-slate-800/40',
      )}
    >
      <div className="mb-3 flex items-center justify-between gap-2 lg:mb-4">
        <span
          className={Join(
            'inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] tracking-widest uppercase lg:text-xs',
            config.tagClass,
          )}
        >
          {config.label}
        </span>
        <span className="font-mono text-[10px] text-slate-500 lg:text-xs dark:text-slate-400">
          {date}
        </span>
      </div>
      <h3 className="mb-2 text-base leading-snug font-light tracking-wide text-slate-800 transition-colors group-hover:text-slate-900 lg:text-lg dark:text-slate-200 dark:group-hover:text-white">
        {title}
      </h3>
      {description ? (
        <p className="grow text-xs leading-relaxed text-slate-600 dark:text-slate-400">
          {description}
        </p>
      ) : (
        <span className="grow" aria-hidden />
      )}
      {highlight ? (
        <span className="mt-3 inline-flex w-fit items-center rounded-full border border-teal-300 bg-teal-50 px-2 py-0.5 font-mono text-[10px] text-teal-600 lg:text-xs dark:border-teal-400/30 dark:bg-teal-400/10 dark:text-teal-400">
          {highlight}
        </span>
      ) : null}
      {source ? (
        <div className="mt-4 flex items-center gap-1.5 font-mono text-[10px] text-slate-500 transition-colors group-hover:text-slate-700 lg:text-xs dark:text-slate-400 dark:group-hover:text-slate-300">
          <ArrowTopRightOnSquareIcon
            className="size-2.5 lg:size-3"
            aria-hidden
          />
          {source}
        </div>
      ) : null}
    </a>
  )
}
